import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supportAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import {
  LifebuoyIcon,
  CheckCircleIcon,
  SparklesIcon,
  ClockIcon,
  ArrowPathIcon,
  BellAlertIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  PencilSquareIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import MetricHint from '../components/common/MetricHint';
import { displayLabel } from '../utils/format';

const statusBadge = {
  open: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  deflected: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-300',
};

const statusLabel = (s) => (s === 'in_progress' ? 'in progress' : s);

// Compact SLA badge from the ticket's computed sla block. Shows the worst
// active breach; null when nothing to show (met, or deflected/no-SLA).
function SlaBadge({ sla }) {
  if (!sla || !sla.applies) return null;
  if (sla.resolution?.breached) {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" title="Past the time you set to resolve it">Overdue fix</span>;
  }
  if (sla.first_response?.breached) {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" title="Past the time you set for a first reply">Overdue reply</span>;
  }
  return null;
}

const priorityBadge = {
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  normal: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  low: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};

const PRIORITIES = ['low', 'normal', 'high', 'urgent'];

const userLabel = (u) => u?.full_name || u?.username || u?.email || 'User';

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function fmt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

// Right-side drawer: full ticket detail + resolve. Fetches GET /support/tickets/{id}.
const TicketDrawer = ({ ticketId, params, users, canManage, onClose, onResolved }) => {
  const [ticket, setTicket] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [assignee, setAssignee] = useState('');
  const [priority, setPriority] = useState('');
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [savingAssign, setSavingAssign] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    Promise.all([
      supportAPI.getTicket(ticketId, params),
      canManage ? supportAPI.getTicketActivity(ticketId, params).catch(() => ({ data: { activity: [] } })) : Promise.resolve({ data: { activity: [] } }),
    ])
      .then(([res, activityRes]) => {
        if (!alive) return;
        setTicket(res.data);
        setActivity(activityRes.data?.activity || []);
        setAssignee(res.data?.assigned_to || '');
        setPriority(res.data?.priority || '');
      })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Could not load ticket'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ticketId, params, canManage]);

  const assignDirty = ticket && ((assignee || '') !== (ticket.assigned_to || '') || (priority || '') !== (ticket.priority || ''));

  const saveAssignment = async () => {
    setSavingAssign(true);
    setError(null);
    try {
      const res = await supportAPI.updateTicket(
        ticketId,
        { assigned_to: assignee || null, priority: priority || null },
        params,
      );
      setTicket(res.data);
      setSavedMsg(true);
      setTimeout(() => setSavedMsg(false), 1500);
      onResolved(); // refresh the list so the row reflects the new assignee/priority
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save assignment');
    } finally {
      setSavingAssign(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    setError(null);
    try {
      const res = await supportAPI.addTicketNote(ticketId, { note: note.trim() }, params);
      setActivity([res.data, ...activity]);
      setNote('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  const deleteTicket = async () => {
    setDeleting(true);
    setError(null);
    try {
      await supportAPI.deleteTicket(ticketId, params);
      onResolved();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to delete ticket');
      setDeleting(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const resolve = async (csat) => {
    setResolving(true);
    try {
      await supportAPI.resolveTicket(ticketId, { csat: csat || null, notify_customer: true }, params);
      onResolved();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to resolve');
      setResolving(false);
    }
  };

  // Lifecycle transitions (in_progress / reopen) + escalate. Refetches so the
  // drawer reflects new status/SLA without closing.
  const [acting, setActing] = useState(false);
  const doAction = async (fn) => {
    setActing(true);
    setError(null);
    try {
      await fn();
      const res = await supportAPI.getTicket(ticketId, params);
      setTicket(res.data);
      onResolved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Action failed');
    } finally {
      setActing(false);
    }
  };
  const setStatus = (status) => doAction(() => supportAPI.updateTicket(ticketId, { status }, params));
  const escalate = () => doAction(() => supportAPI.escalateTicket(ticketId, {}, params));

  const Row = ({ label, children }) => (
    <div className="flex justify-between gap-4 py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <span className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 flex-shrink-0">{label}</span>
      <span className="text-sm text-gray-800 dark:text-gray-100 text-right break-words">{children || '—'}</span>
    </div>
  );

  const cf = ticket?.custom_fields && typeof ticket.custom_fields === 'object' ? ticket.custom_fields : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg h-full bg-white dark:bg-gray-800 shadow-2xl flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <LifebuoyIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                {loading ? 'Loading…' : (ticket?.subject || 'Support ticket')}
              </h3>
            </div>
            {ticket && (
              <span className="inline-flex items-center gap-1.5 mt-1.5">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadge[ticket.status] || ''}`}>
                  {statusLabel(ticket.status)}
                </span>
                <SlaBadge sla={ticket.sla} />
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">Loading ticket…</p>}
          {error && <p className="text-sm text-red-600 dark:text-red-400 py-8 text-center">{error}</p>}
          {ticket && !loading && (
            <div className="space-y-5">
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">Issue</h4>
                <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words bg-gray-50 dark:bg-gray-900/40 rounded-lg p-3">
                  {ticket.body || 'No description provided.'}
                </p>
              </section>

              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Customer</h4>
                <Row label="Name">{ticket.name || 'Anonymous'}</Row>
                <Row label="Email">{ticket.email}</Row>
                <Row label="Phone">{ticket.phone}</Row>
              </section>

              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Details</h4>
                <Row label="Channel">{ticket.channel}</Row>
                <Row label="Source">{ticket.source}</Row>
                <Row label="Agent">{ticket.agent_name || ticket.agent_id}</Row>
                <Row label="Conversation">
                  {ticket.conversation_id
                    ? <span className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400"><ChatBubbleLeftRightIcon className="w-3.5 h-3.5" />{String(ticket.conversation_id).slice(0, 12)}…</span>
                    : '—'}
                </Row>
                <Row label="Created">{fmt(ticket.created_at)}</Row>
                <Row label="Updated">{fmt(ticket.updated_at)}</Row>
                <Row label="First response">{fmt(ticket.first_response_at)}</Row>
                {ticket.resolved_at && <Row label="Resolved">{fmt(ticket.resolved_at)}</Row>}
                {ticket.escalated_at && <Row label="Escalated">{fmt(ticket.escalated_at)}</Row>}
                {ticket.reopen_count > 0 && <Row label="Reopened">{ticket.reopen_count}×</Row>}
                {ticket.sla?.applies && (
                  <Row label="Response-time target">
                    {ticket.sla.breached
                      ? <span className="text-red-600 dark:text-red-400">Overdue ({displayLabel(ticket.sla.priority)} priority)</span>
                      : <span className="text-green-600 dark:text-green-400">On track ({displayLabel(ticket.sla.priority)} priority)</span>}
                  </Row>
                )}
              </section>

              <section className={canManage ? '' : 'hidden'}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Assignment</h4>
                  {savedMsg && <span className="text-xs text-green-600 dark:text-green-400">Saved</span>}
                </div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Assigned to</label>
                <select
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  className="w-full mb-3 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Unassigned</option>
                  {(users || []).map((u) => (
                    <option key={u.id} value={u.id}>{userLabel(u)}</option>
                  ))}
                </select>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full mb-3 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 capitalize"
                >
                  <option value="">None</option>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <button
                  disabled={!assignDirty || savingAssign}
                  onClick={saveAssignment}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
                >
                  {savingAssign ? 'Saving…' : 'Save assignment'}
                </button>
              </section>

              <section className={canManage ? '' : 'hidden'}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">Internal notes</h4>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Add an internal note for your team..."
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
                <button
                  disabled={!note.trim() || savingNote}
                  onClick={addNote}
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
                >
                  <PencilSquareIcon className="w-4 h-4" />
                  {savingNote ? 'Saving...' : 'Add note'}
                </button>
              </section>

              {cf && Object.keys(cf).length > 0 && (
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Additional</h4>
                  {Object.entries(cf).map(([k, v]) => (
                    <Row key={k} label={k.replace(/_/g, ' ')}>
                      {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </Row>
                  ))}
                </section>
              )}

              {ticket.status === 'resolved' && (
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Resolution</h4>
                  <Row label="Note">{ticket.resolution_note}</Row>
                  <Row label="Customer rating">{ticket.csat === 'up' ? '👍 Positive' : ticket.csat === 'down' ? '👎 Negative' : '—'}</Row>
                </section>
              )}

              <section className={canManage ? '' : 'hidden'}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">Activity</h4>
                {activity.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No internal activity yet.</p>
                ) : (
                  <div className="space-y-2">
                    {activity.map((item) => (
                      <div key={item.id} className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                            {String(item.event_type || '').replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">{fmt(item.created_at)}</span>
                        </div>
                        {item.note && <p className="mt-1 text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{item.note}</p>}
                        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{item.actor_name || 'System'}</p>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className={`${canManage ? '' : 'hidden'} rounded-lg border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-900/20 p-3`}>
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-semibold text-red-800 dark:text-red-200">Delete ticket</h4>
                    <p className="mt-1 text-xs text-red-700 dark:text-red-300">
                      Removes this ticket but keeps a content-free deletion audit.
                    </p>
                    {confirmingDelete ? (
                      <div className="mt-3 flex items-center gap-2">
                        <button
                          disabled={deleting}
                          onClick={() => setConfirmingDelete(false)}
                          className="px-3 py-1.5 text-sm rounded-lg border border-red-300 text-red-700 hover:bg-red-100 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          disabled={deleting}
                          onClick={deleteTicket}
                          className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          {deleting ? 'Deleting...' : 'Delete permanently'}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmingDelete(true)}
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg text-red-700 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-900/40"
                      >
                        <TrashIcon className="w-4 h-4" />
                        Delete ticket
                      </button>
                    )}
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>

        {ticket && !loading && (ticket.status === 'open' || ticket.status === 'in_progress') && (
          <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
            {canManage && (
              <div className="flex flex-wrap items-center gap-2">
                {ticket.status === 'open' && (
                  <button disabled={acting} onClick={() => setStatus('in_progress')}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                    Start (in progress)
                  </button>
                )}
                {ticket.priority !== 'urgent' && (
                  <button disabled={acting} onClick={escalate}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-900/60 dark:text-red-300 dark:hover:bg-red-900/30 disabled:opacity-50">
                    <ExclamationTriangleIcon className="w-4 h-4" /> Escalate
                  </button>
                )}
              </div>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">Mark resolved and (optionally) record how the customer felt:</p>
            <div className="flex items-center gap-2">
              <button disabled={resolving} onClick={() => resolve('up')}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
                <HandThumbUpIcon className="w-4 h-4" /> Resolve · Positive
              </button>
              <button disabled={resolving} onClick={() => resolve('down')}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                <HandThumbDownIcon className="w-4 h-4" /> Resolve · Negative
              </button>
              <button disabled={resolving} onClick={() => resolve(null)}
                className="px-3 py-2 text-sm rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                Resolve
              </button>
            </div>
          </div>
        )}
        {ticket && !loading && ticket.status === 'resolved' && canManage && (
          <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700">
            <button disabled={acting} onClick={() => setStatus('open')}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
              <ArrowPathIcon className="w-4 h-4" /> Reopen ticket
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const SupportPage = () => {
  const { tenantId } = useParams();
  const params = React.useMemo(() => (tenantId ? { tenant_id: tenantId } : {}), [tenantId]);
  const currentUser = useAuthStore((state) => state.user);
  const isSuperadmin = currentUser?.role === 'superadmin' || currentUser?.is_superuser;
  const canManage = isSuperadmin || currentUser?.tenant_role === 'admin' || currentUser?.tenant_role === 'owner';

  const [metrics, setMetrics] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reminderMsg, setReminderMsg] = useState(null);
  const [openTicketId, setOpenTicketId] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [bulkPriority, setBulkPriority] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState(null);

  // Assignable tenant users for the assignee dropdown / list display (fetched once).
  useEffect(() => {
    if (!canManage) {
      setUsers([]);
      return;
    }
    supportAPI.getAssignableUsers(params)
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : (res.data?.users || [])))
      .catch(() => { /* dropdown just shows Unassigned */ });
  }, [params, canManage]);

  const userById = React.useMemo(
    () => Object.fromEntries((users || []).map((u) => [u.id, u])),
    [users],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const listParams = { ...params, limit: 100 };
      if (statusFilter && statusFilter !== 'all') listParams.status = statusFilter;
      if (assigneeFilter) listParams.assigned_to = assigneeFilter;
      if (priorityFilter) listParams.priority = priorityFilter;
      const [metricsRes, ticketsRes] = await Promise.all([
        supportAPI.getMetrics(params),
        supportAPI.getTickets(listParams),
      ]);
      setMetrics(metricsRes.data);
      setTickets(ticketsRes.data.tickets || []);
      setSelectedIds([]);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  }, [params, statusFilter, assigneeFilter, priorityFilter]);

  useEffect(() => { load(); }, [load]);

  const handleRunReminders = async () => {
    setReminderMsg('Running…');
    try {
      const res = await supportAPI.runReminders(params);
      setReminderMsg(`Reminders sent: ${res.data.sent}, skipped: ${res.data.skipped}`);
    } catch (err) {
      setReminderMsg(err?.response?.data?.detail || 'Reminder run failed');
    }
  };

  const currentFilterParams = () => {
    const exportParams = { ...params };
    if (statusFilter && statusFilter !== 'all') exportParams.status = statusFilter;
    if (assigneeFilter) exportParams.assigned_to = assigneeFilter;
    if (priorityFilter) exportParams.priority = priorityFilter;
    return exportParams;
  };

  const exportCsv = async () => {
    const res = await supportAPI.exportTicketsCsv(currentFilterParams());
    saveBlob(res.data, `serviti-support-tickets-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const exportJson = async () => {
    const res = await supportAPI.exportTicketsJson(currentFilterParams());
    saveBlob(
      new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' }),
      `serviti-support-tickets-${new Date().toISOString().slice(0, 10)}.json`,
    );
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const visibleIds = tickets.map((t) => t.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const runBulkUpdate = async (payload) => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    setBulkMsg(null);
    try {
      const res = await supportAPI.bulkUpdateTickets({ ticket_ids: selectedIds, ...payload }, params);
      setBulkMsg(`Updated ${res.data.updated} of ${res.data.matched} selected tickets.`);
      setBulkAssignee('');
      setBulkPriority('');
      await load();
    } catch (err) {
      setBulkMsg(err?.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected support ticket(s)? This keeps a content-free deletion audit.`)) return;
    setBulkBusy(true);
    setBulkMsg(null);
    try {
      const res = await supportAPI.bulkDeleteTickets(selectedIds, params);
      setBulkMsg(`Deleted ${res.data.deleted} selected tickets.`);
      await load();
    } catch (err) {
      setBulkMsg(err?.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter('open');
    setAssigneeFilter('');
    setPriorityFilter('');
  };

  const tiles = [
    {
      label: 'Open',
      value: metrics ? (metrics.unresolved ?? (metrics.open || 0) + (metrics.in_progress || 0)) : '—',
      icon: LifebuoyIcon,
      tone: 'text-amber-600',
      hint: metrics ? `${metrics.in_progress || 0} in progress` : undefined,
      tooltip: 'Tickets not resolved yet, including ones your team is working on. Your team gets reminders at 2 and 24 hours if your plan includes them.',
    },
    {
      label: 'Resolved',
      value: metrics?.resolved ?? '—',
      icon: CheckCircleIcon,
      tone: 'text-green-600',
      tooltip: 'Tickets your team has marked resolved. If the customer rated the help, it counts toward customer satisfaction.',
    },
    {
      label: 'Answered by AI alone',
      value: metrics?.deflection_rate != null ? `${Math.round(metrics.deflection_rate * 100)}%` : '—',
      icon: SparklesIcon,
      tone: 'text-sky-600',
      hint: 'No one on your team needed',
      tooltip: 'Share of support questions your agent answered without your team: no ticket needed and the customer was satisfied.',
    },
    {
      label: 'Average time to resolve',
      value: metrics?.avg_resolution_hours != null ? `${metrics.avg_resolution_hours}h` : '—',
      icon: ClockIcon,
      tone: 'text-purple-600',
      tooltip: 'Average time from ticket opened to ticket resolved, across all resolved tickets in the window. Lower = faster customer experience.',
    },
  ];

  const slaTiles = [
    { label: 'In progress', value: metrics?.in_progress ?? 0, tone: 'text-blue-700 dark:text-blue-300' },
    { label: 'Waiting 24h+', value: metrics?.sla?.open_24h_plus ?? 0, tone: 'text-amber-700 dark:text-amber-300' },
    { label: 'Past response-time target', value: (metrics?.sla?.first_response_breached ?? 0) + (metrics?.sla?.resolution_breached ?? 0), tone: 'text-red-700 dark:text-red-300' },
    {
      label: 'Customer satisfaction',
      value: metrics?.csat?.rate != null ? `${Math.round(metrics.csat.rate * 100)}%` : '—',
      tone: 'text-green-700 dark:text-green-300',
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <LifebuoyIcon className="w-7 h-7 text-amber-600" /> Support tickets
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Post-sale issues raised by your existing customers. Click a ticket to see the full details.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ArrowDownTrayIcon className="w-4 h-4" /> Export spreadsheet
          </button>
          )}
          {isSuperadmin && (
          <button
            onClick={exportJson}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ArrowDownTrayIcon className="w-4 h-4" /> Export JSON
          </button>
          )}
          {canManage && (
          <button
            onClick={handleRunReminders}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <BellAlertIcon className="w-4 h-4" /> Send reminders now
          </button>
          )}
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ArrowPathIcon className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {reminderMsg && (
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-300">{reminderMsg}</div>
      )}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 text-sm">{error}</div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {tiles.map((t) => (
          <div key={t.label} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                {t.label}
                {t.tooltip && <MetricHint text={t.tooltip} />}
              </span>
              <t.icon className={`w-5 h-5 ${t.tone}`} />
            </div>
            <div className="text-2xl font-semibold text-gray-900 dark:text-white mt-2">{t.value}</div>
            {t.hint && <div className="text-xs text-gray-400 mt-1">{t.hint}</div>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        {slaTiles.map((t) => (
          <div key={t.label} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">{t.label}</div>
            <div className={`mt-1 text-xl font-semibold ${t.tone}`}>{t.value}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {['open', 'in_progress', 'resolved', 'all'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-sm rounded-lg capitalize ${
              statusFilter === s
                ? 'bg-amber-600 text-white'
                : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {statusLabel(s)}
          </button>
        ))}
        <select
          value={assigneeFilter}
          onChange={(e) => setAssigneeFilter(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200"
        >
          <option value="">All assignees</option>
          <option value="unassigned">Unassigned</option>
          {(users || []).map((u) => (
            <option key={u.id} value={u.id}>{userLabel(u)}</option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 capitalize"
        >
          <option value="">All priorities</option>
          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        {(statusFilter !== 'open' || assigneeFilter || priorityFilter) && (
          <button onClick={clearFilters} className="px-3 py-1.5 text-sm rounded-lg text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700">
            Clear
          </button>
        )}
      </div>

      {canManage && selectedIds.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/60 dark:bg-amber-900/20">
          <span className="text-sm font-medium text-amber-900 dark:text-amber-100">{selectedIds.length} selected</span>
          <select
            value={bulkAssignee}
            onChange={(e) => setBulkAssignee(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-amber-200 bg-white text-gray-700 dark:border-amber-900/60 dark:bg-gray-900 dark:text-gray-100"
          >
            <option value="">Assign to...</option>
            <option value="__unassigned">Unassign</option>
            {(users || []).map((u) => (
              <option key={u.id} value={u.id}>{userLabel(u)}</option>
            ))}
          </select>
          <button
            disabled={bulkBusy || !bulkAssignee}
            onClick={() => runBulkUpdate({ assigned_to: bulkAssignee === '__unassigned' ? null : bulkAssignee })}
            className="px-3 py-1.5 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40"
          >
            Apply assignee
          </button>
          <select
            value={bulkPriority}
            onChange={(e) => setBulkPriority(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-amber-200 bg-white text-gray-700 dark:border-amber-900/60 dark:bg-gray-900 dark:text-gray-100 capitalize"
          >
            <option value="">Priority...</option>
            <option value="__none">Clear priority</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <button
            disabled={bulkBusy || !bulkPriority}
            onClick={() => runBulkUpdate({ priority: bulkPriority === '__none' ? null : bulkPriority })}
            className="px-3 py-1.5 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40"
          >
            Apply priority
          </button>
          <button
            disabled={bulkBusy}
            onClick={() => runBulkUpdate({ status: 'in_progress' })}
            className="px-3 py-1.5 text-sm rounded-lg border border-blue-300 text-blue-800 hover:bg-blue-100 disabled:opacity-40 dark:text-blue-100"
          >
            Mark in progress
          </button>
          <button
            disabled={bulkBusy}
            onClick={() => runBulkUpdate({ status: 'resolved' })}
            className="px-3 py-1.5 text-sm rounded-lg border border-amber-300 text-amber-800 hover:bg-amber-100 disabled:opacity-40 dark:text-amber-100"
          >
            Mark resolved
          </button>
          <button
            disabled={bulkBusy}
            onClick={runBulkDelete}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg text-red-700 hover:bg-red-100 disabled:opacity-40 dark:text-red-300"
          >
            <TrashIcon className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}
      {bulkMsg && <div className="mb-3 text-sm text-gray-600 dark:text-gray-300">{bulkMsg}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>
        ) : tickets.length === 0 ? (
          <div className="p-10 text-center">
            <LifebuoyIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
            <h3 className="mt-3 text-base font-semibold text-gray-900 dark:text-white">
              {statusFilter !== 'open' || assigneeFilter || priorityFilter ? 'No tickets match these filters' : 'No open support tickets'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {statusFilter !== 'open' || assigneeFilter || priorityFilter
                ? 'Try clearing filters or switching to All.'
                : 'When an existing customer asks for help, tickets will appear here for follow-up.'}
            </p>
            {(statusFilter !== 'open' || assigneeFilter || priorityFilter) && (
              <button onClick={clearFilters} className="mt-4 px-3 py-2 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700">
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400">
                <tr>
                  {canManage && <th className="px-4 py-3 font-medium w-10">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                      className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      aria-label="Select all visible tickets"
                    />
                  </th>}
                  <th className="text-left px-4 py-3 font-medium">Customer</th>
                  <th className="text-left px-4 py-3 font-medium">Subject</th>
                  <th className="text-left px-4 py-3 font-medium">Priority</th>
                  <th className="text-left px-4 py-3 font-medium">Assignee</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-left px-4 py-3 font-medium">Age</th>
                  <th className="text-right px-4 py-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {tickets.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => setOpenTicketId(t.id)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                  >
                    {canManage && <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(t.id)}
                        onChange={() => toggleSelected(t.id)}
                        className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                        aria-label={`Select ticket ${t.subject || t.id}`}
                      />
                    </td>}
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-white">{t.name}</div>
                      <div className="text-xs text-gray-400">{t.phone || t.email || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                      <div className="font-medium">{t.subject || '—'}</div>
                      <div className="text-xs text-gray-400 truncate max-w-xs">{t.body}</div>
                    </td>
                    <td className="px-4 py-3">
                      {t.priority ? (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${priorityBadge[t.priority] || ''}`}>
                          {displayLabel(t.priority)}
                        </span>
                      ) : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {t.assigned_to
                        ? <span className="text-xs">{userById[t.assigned_to] ? userLabel(userById[t.assigned_to]) : 'Assigned'}</span>
                        : <span className="text-xs text-gray-400">Unassigned</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadge[t.status] || ''}`}>
                          {statusLabel(t.status)}
                        </span>
                        <SlaBadge sla={t.sla} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {t.age_hours != null ? `${t.age_hours}h` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); setOpenTicketId(t.id); }}
                        className="text-sm font-medium text-amber-700 dark:text-amber-400 hover:underline"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openTicketId && (
        <TicketDrawer
          ticketId={openTicketId}
          params={params}
          users={users}
          canManage={canManage}
          onClose={() => setOpenTicketId(null)}
          onResolved={load}
        />
      )}
    </div>
  );
};

export default SupportPage;
